import { useEffect, useState } from "react";
import { ImageIcon } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ObsTileFields } from "./ObsTileFields";
import {
  EMPTY_SIZES,
  EMPTY_TILES,
  measureImage,
  type TileFiles,
  type TileLocale,
  type TileSizes,
} from "@/features/obs/lib/tiles";
import { useUploadObsTile } from "@/features/obs/hooks/useObsQueries";
import { handleApiError } from "@/lib/error-handler";
import type { ObsReport } from "@/types/obs";

/**
 * Attach or replace the tiles that open this report from the OBS landing page.
 *
 * Available for EVERY report, including the six. They need it most: their rows are
 * created by `scripts/seed-obs-reports.js`, which deliberately does not attach
 * artwork — the pairing between a migrated image and a report is not something a
 * script can verify, and guessing it would publish one report's picture under
 * another's name. A person matching them by eye, once, is the safe version.
 */
export function ObsTileDialog({
  report,
  open,
  onOpenChange,
}: {
  report: ObsReport;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [files, setFiles] = useState<TileFiles>(EMPTY_TILES);
  const [sizes, setSizes] = useState<TileSizes>(EMPTY_SIZES);
  const upload = useUploadObsTile();

  useEffect(() => {
    if (!open) return;
    setFiles(EMPTY_TILES);
    setSizes(EMPTY_SIZES);
    upload.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (locale: TileLocale, picked: File | null) => {
    setFiles((f) => ({ ...f, [locale]: picked }));
    setSizes((s) => ({ ...s, [locale]: null }));
    if (picked)
      measureImage(picked, (size) =>
        setSizes((s) => ({ ...s, [locale]: size })),
      );
  };

  /** Uploaded one at a time — each locale is its own field on the report. */
  const save = async () => {
    try {
      for (const locale of ["ar", "en"] as TileLocale[]) {
        const f = files[locale];
        if (f) await upload.mutateAsync({ slug: report.slug, file: f, locale });
      }
      onOpenChange(false);
    } catch (e) {
      handleApiError(e);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>صورة البطاقة — {report.title.ar}</DialogTitle>
          <DialogDescription>
            الصور التي تفتح هذا التقرير من صفحة تقارير الموازنة المفتوحة.
          </DialogDescription>
        </DialogHeader>

        <ObsTileFields
          sizes={sizes}
          disabled={upload.isPending}
          current={{ ar: report.tile, en: report.tileEn }}
          onPick={pick}
        />

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={upload.isPending}
          >
            إلغاء
          </Button>
          <Button
            onClick={save}
            disabled={(!files.ar && !files.en) || upload.isPending}
          >
            {upload.isPending ? <Loader /> : <ImageIcon className="size-4" />}
            حفظ الصور
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
